import React, {createContext, ReactNode, useContext, useEffect, useRef, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {DecryptedContentVersionDto, DecryptedThread, useThread} from '../hooks/useThread';
import {ContentsClient, CreateContentDto, SaveDraftDto} from '../api/api-client';
import {CryptoHelper} from '../utils/cryptoHelper';
import {useAuth} from "./AuthContext";

interface ThreadSettings {
  enableThinking: boolean;
  enableWebSearch: boolean;
  isAiMode: boolean;
}

interface ThreadContextType {
  // Data
  thread?: DecryptedThread;
  isLoading: boolean;
  messages: DecryptedContentVersionDto[];
  contentKey?: CryptoKey;
  currentDraftId?: number | null;
  currentDraftContent?: string;
  decryptedTitle?: string;
  
  // Thread Settings
  enableThinking: boolean;
  enableWebSearch: boolean;
  isAiMode: boolean;
  
  // Mutations
  saveDraft: (id: number | undefined, content: string | undefined) => Promise<DecryptedContentVersionDto>;
  deleteMessage: (messageId: number) => Promise<void>;
  updateTitle: (encryptedTitle: string) => Promise<void>;
  createThread: (title: string) => Promise<DecryptedThread>;
  
  // Settings Actions
  setThinkingEnabled: (enabled: boolean) => void;
  setWebSearchEnabled: (enabled: boolean) => void;
  setAiModeEnabled: (enabled: boolean) => void;
  
  // Actions
  refetch: () => void;
}

const ThreadContext = createContext<ThreadContextType | undefined>(undefined);

interface ThreadProviderProps {
  threadId?: number;
  children: ReactNode;
}

const defaultThreadSettings: ThreadSettings = {
  enableThinking: false,
  enableWebSearch: false,
  isAiMode: true,
};

export const ThreadProvider: React.FC<ThreadProviderProps> = ({ threadId, children }) => {
  const {publicKey} = useAuth();
  const queryClient = useQueryClient();
  
  // Thread-specific settings stored in localStorage by thread ID
  const [threadSettings, setThreadSettingsInternal] = useState<ThreadSettings>(defaultThreadSettings);
  
  const setThreadSettings = (newSettings: ThreadSettings | ((prev: ThreadSettings) => ThreadSettings)) => {
    setThreadSettingsInternal(newSettings);
  };
  
  // Ref to store immediate access to content key and thread ID after creation before we load the thread from the server again.
  const immediateStateRef = useRef<{
    contentKey?: CryptoKey;
    threadId?: number;
    thread?: DecryptedThread;
  }>({});
  
  // Ref to track if settings have been initialized for the current thread
  const settingsInitializedRef = useRef<{ threadId?: number; initialized: boolean }>({ initialized: false });

  // Fetch thread data
  const { data: thread, isLoading, refetch } = useThread(threadId, {
    enabled: !!threadId
  });

  // Load thread settings from localStorage when threadId changes
  useEffect(() => {
    if (threadId) {
      // Always check if this is a different thread ID than what we have initialized
      if (settingsInitializedRef.current.threadId !== threadId) {
        const settingsKey = `threadSettings_${threadId}`;
        const savedSettings = localStorage.getItem(settingsKey);
        
        if (savedSettings) {
          try {
            const parsed = JSON.parse(savedSettings);
            const mergedSettings = { ...defaultThreadSettings, ...parsed };
            setThreadSettingsInternal(mergedSettings);
          } catch (error) {
            console.error('Failed to parse thread settings:', error);
            setThreadSettingsInternal(defaultThreadSettings);
          }
        } else {
          setThreadSettingsInternal(defaultThreadSettings);
        }
        
        // Mark as initialized for this thread
        settingsInitializedRef.current = { threadId, initialized: true };
      }
    } else {
      // No threadId (new thread) - reset to defaults for fresh start
      setThreadSettingsInternal(defaultThreadSettings);
      settingsInitializedRef.current = { initialized: false };
    }
  }, [threadId]);

  // Save thread settings to localStorage whenever they change
  useEffect(() => {
    if (threadId) {
      const settingsKey = `threadSettings_${threadId}`;
      localStorage.setItem(settingsKey, JSON.stringify(threadSettings));
    }
  }, [threadId, threadSettings]);

  // Clear ref when threadId prop changes (navigating between threads and load is completed)
  useEffect(() => {
    // We've navigated to a new thread, clear the immediate state
    if ((threadId === thread?.id) && immediateStateRef.current?.threadId === threadId) {
      immediateStateRef.current = {};
    }
  }, [threadId, isLoading, thread]);
  
  // Mutations
  const saveDraftMutation = useMutation({
    mutationFn: async (draftData: SaveDraftDto) => {
      const savingThreadId = immediateStateRef.current.threadId ?? threadId;
      if (!savingThreadId) throw new Error('Cannot save draft without thread ID');
      const contentsClient = new ContentsClient();
      return await contentsClient.saveDraft(savingThreadId, draftData);
    },
    onSuccess: (result, variables) => {
      // Only invalidate for submitted messages, not drafts
      if (variables.isSubmitted || variables.isCompleted) {
        const savingThreadId = immediateStateRef.current.threadId ?? threadId;
        if (savingThreadId) {
          queryClient.invalidateQueries({ queryKey: ['thread', savingThreadId] });
        }
      }
      // For drafts (just saving while typing), don't invalidate - they'll be picked up on next load
    }
  });

  const deleteMessageMutation = useMutation({
    mutationFn: async (messageId: number) => {
      if (!threadId) throw new Error('Cannot delete message without thread ID');
      const contentsClient = new ContentsClient();
      await contentsClient.deprecateMessage(threadId, messageId, { newEncryptedData: undefined });
    },
    onSuccess: () => {
      if (threadId) {
        queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
      }
    }
  });

  const updateTitleMutation = useMutation({
    mutationFn: async (encryptedTitle: string) => {
      const saveThreadId = immediateStateRef.current.threadId ?? threadId;
      if (!saveThreadId) throw new Error('Cannot update title without thread ID');
      const contentsClient = new ContentsClient();
      await contentsClient.updateTitle(saveThreadId, encryptedTitle);
    },
    onSuccess: () => {
      if (threadId) {
        queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
        queryClient.invalidateQueries({ queryKey: ['contents'] }); // Update sidebar
      }
    }
  });

  const createThreadMutation = useMutation({
    mutationFn: async (createContentDto: CreateContentDto) => {
      const contentsClient = new ContentsClient();
      return await contentsClient.createContent(createContentDto);
    },
    onSuccess: () => {
      // Don't invalidate contents here - SignalR ContentCreated event will handle it
    }
  });
  
  const createThread = async (title: string) => {
     if (!publicKey) throw new Error('Cannot create thread without user public key');

     // New threads always get new content keys.
     const contentKey = await CryptoHelper.generateContentKey();

     // Encrypt the content key with the user's public key
     const encryptedContentKey = await CryptoHelper.encryptContentKey(contentKey, publicKey);

     // Encrypt the title with the content key
     const encryptedTitle = await CryptoHelper.encryptContent(title, contentKey);

     // Create the content DTO - this has the encrypted content key because the server will auto-add us to the thread as owner.
     const createContentDto: CreateContentDto = {
       title: encryptedTitle,
       contentType: 'AIThread',
       encryptedContentKey: encryptedContentKey
     };

     const newThread: DecryptedThread = {
       ...await createThreadMutation.mutateAsync(createContentDto),
       decryptedTitle: title,
       contentKey: contentKey,
       currentDraftId: null,
       currentDraftContent: undefined,
     }

     // Update ref immediately after creation for immediate access
     immediateStateRef.current.thread = newThread;
     immediateStateRef.current.contentKey = contentKey;
     immediateStateRef.current.threadId = newThread.id;

     // When creating a new thread, immediately save the current settings to localStorage
     // This ensures the UI settings are preserved for the new thread
     if (newThread.id) {
       const settingsKey = `threadSettings_${newThread.id}`;
       localStorage.setItem(settingsKey, JSON.stringify(threadSettings));
       settingsInitializedRef.current = { threadId: newThread.id, initialized: true };
     }

     return newThread;
   };
  
  const saveDraft = async (draftId: number | undefined, content: string | undefined): Promise<DecryptedContentVersionDto> => {
    const savingThreadId = immediateStateRef.current.threadId ?? threadId;
    const savingThread = immediateStateRef.current.thread ?? thread;
    const savingContentKey = immediateStateRef.current.contentKey || savingThread?.contentKey;

    if (!savingThreadId) throw new Error('Cannot save draft without thread');
    if (!savingContentKey) throw new Error('No content key available for encryption');

    // Encrypt the content - use a space for empty content to avoid deletion
    const encryptedData = content?.trim() ?
       await CryptoHelper.encryptContent(content!, savingContentKey) :
       await CryptoHelper.encryptContent(' ', savingContentKey);

    const saveDraftDto: SaveDraftDto = {
      draftId: draftId,
      encryptedData: encryptedData,
      messageType: 'user', // drafts are always user messages
      isSubmitted: false,
      isCompleted: false
    };
    return await saveDraftMutation.mutateAsync(saveDraftDto);
  }

  // Thread settings functions
  const setThinkingEnabled = (enabled: boolean) => {
    setThreadSettings(prev => ({ ...prev, enableThinking: enabled }));
  };

  const setWebSearchEnabled = (enabled: boolean) => {
    setThreadSettings(prev => ({ ...prev, enableWebSearch: enabled }));
  };

  const setAiModeEnabled = (enabled: boolean) => {
    setThreadSettings(prev => ({ ...prev, isAiMode: enabled }));
  };

  const contextValue: ThreadContextType = {
    // Data
    isLoading,
    thread: immediateStateRef.current.thread ?? thread,
    contentKey: immediateStateRef.current.contentKey || thread?.contentKey,
    messages: thread?.decryptedMessages || [],
    currentDraftId: thread?.currentDraftId,
    currentDraftContent: thread?.currentDraftContent,
    decryptedTitle: thread?.decryptedTitle,
    
    // Thread Settings
    enableThinking: threadSettings.enableThinking,
    enableWebSearch: threadSettings.enableWebSearch,
    isAiMode: threadSettings.isAiMode,
    
    // Mutations
    saveDraft:  saveDraft, 
    deleteMessage: deleteMessageMutation.mutateAsync,
    updateTitle: updateTitleMutation.mutateAsync,
    createThread: createThread,
    
    // Settings Actions
    setThinkingEnabled,
    setWebSearchEnabled,
    setAiModeEnabled,
    
    // Actions
    refetch
  };

  return (
    <ThreadContext.Provider value={contextValue}>
      {children}
    </ThreadContext.Provider>
  );
};

export const useThreadContext = () => {
  const context = useContext(ThreadContext);
  if (context === undefined) {
    throw new Error('useThreadContext must be used within a ThreadProvider');
  }
  return context;
};

export { ThreadContext };
export type { ThreadContextType };